/**
 * Custom hook for request file search using Elasticsearch
 * 
 * Supports filtering, pagination, and full-text search
 */
import { useQuery } from '@tanstack/react-query'
import {
  searchRequestFiles,
  type RequestFileSearchFilters,
  type RequestFileSearchResponse,
} from '../api/requestFileSearchApi'

/**
 * Hook for searching request files with Elasticsearch
 * 
 * @param filters - Search filters
 * @param options - React Query options
 * @returns Query result with request file data
 */
export function useRequestFileSearch(
  filters: RequestFileSearchFilters | null,
  options: {
    enabled?: boolean
    staleTime?: number
  } = {}
) {
  const safeFilters: RequestFileSearchFilters = filters ?? { q: '' }
  const { enabled = true, staleTime = 30000 } = options

  const query = useQuery<RequestFileSearchResponse, Error>({
    queryKey: ['request-file-search', safeFilters],
    queryFn: () => searchRequestFiles(safeFilters),
    enabled: enabled && !!safeFilters.q && safeFilters.q.trim().length > 0,
    staleTime,
    retry: 1,
    refetchOnWindowFocus: false,
  })

  return {
    ...query,
    files: query.data?.results ?? [],
    total: query.data?.total ?? 0,
    maxScore: query.data?.max_score,
    hasMore: query.data?.page?.has_more ?? false,
    currentPage: Math.floor((safeFilters.from ?? 0) / (safeFilters.size ?? 20)),
  }
}

// Re-export types
export type { RequestFileSearchFilters, RequestFileSearchResponse }
